import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';
import { ApiOrder } from '../orders/models/order.models';

export interface CancellationRequest {
  cancelled_by: string;
  cancellation_reason: string;
}

export interface CancellationResponse {
  status: boolean;
  status_code: number;
  message: string;
  data: ApiOrder;
}

/**
 * Service responsible for cancelling orders by calling the backend API.
 * Provides a single convenient method to request order cancellation and returns the API response as an observable.
 * @service
 * @providedIn root
 * @example
 *   // Inject and use from a component or another service
 *   constructor(private cancellationService: CancellationService) {}
 *   this.cancellationService.cancelOrder('orderId123', 'Customer requested cancellation')
 *     .subscribe(response => {
 *       // handle successful cancellation
 *     }, error => {
 *       // handle error
 *     });
 * @dependency ApiService apiService - Injected HTTP wrapper used to perform PUT requests to the backend.
 * @input none - This injectable does not expose input properties; it exposes actions (methods) instead.
 * @output Observable<ApiResponse<CancellationResponse>> cancelOrder - Observable that emits the API response for the cancellation request or an error if the request fails.
 * @method cancelOrder(orderId: string, reason: string) - Sends a PUT request to the endpoint `order/cancel/{orderId}` with a body containing cancelled_by: 'admin' and cancellation_reason set to the provided reason. Returns an Observable of the API response (CancellationResponse wrapped in ApiResponse).
 * @lifecycle None - This service does not implement Angular lifecycle hooks.
 */
@Injectable({
  providedIn: 'root'
})
export class CancellationService {
  constructor(private apiService: ApiService) {}

  cancelOrder(orderId: string, reason: string): Observable<ApiResponse<CancellationResponse>> {
    const requestBody: CancellationRequest = {
      cancelled_by: 'admin',
      cancellation_reason: reason
    };

    return this.apiService.put<CancellationResponse>(`order/cancel/${orderId}`, requestBody);
  }
}
